<template>
	<v-container
		fluid
		pa-0
	>
		<v-form
			ref="form"
			v-if="output"
			v-model="valid"
			@submit="submit"
		>
			<div v-if="!!this.$slots['output-header']">
				<slot name="output-header" />
			</div>
			<div
				v-if="$userCan('change_output_settings')"
				:class="{ 'setting-item': !this.$slots['output-header']}"
			>
				<div class="title my-2">Output Settings</div>
				<div class="setting-item">
					<v-select
						v-model="output.articleDisplay"
						:rules="[v => !!v || 'Field is required']"
						:items="articleDisplays"
						item-text="name"
						item-value="value"
						label="Article Settings *"
					/>
				</div>
				<v-checkbox
					v-model="output.config.sortByImportanceFirst"
					hide-details
					label="Sort By Importance First"
					color="primary"
					class="ma-0"
				/>
				<v-checkbox
					v-model="output.groupByCategory"
					hide-details
					label="Group by Category"
					color="primary"
					class="ma-0"
				/>
				<v-checkbox
					v-model="output.config.showIndex"
					hide-details
					label="Show Index"
					color="primary"
					class="ma-0"
				/>
				<v-checkbox
					v-model="output.config.showImages"
					hide-details
					label="Show Images"
					color="primary"
					class="ma-0"
				/>
				<v-checkbox
					v-model="output.config.showAuthor"
					hide-details
					label="Show Author"
					color="primary"
					class="ma-0"
				/>
				<v-checkbox
					v-model="output.config.showSocialStats"
					hide-details
					label="Show Social Stats"
					color="primary"
					class="ma-0"
				/>
				<v-checkbox
					v-model="output.config.showParentCategories"
					hide-details
					label="Show Parent Categories"
					color="primary"
					class="ma-0"
				/>
				<v-checkbox
					v-model="output.config.showHeaderMetrics"
					hide-details
					label="Show Header Metrics"
					color="primary"
					class="ma-0"
				/>
				<v-checkbox
					v-model="output.config.showReprints"
					hide-details
					label="Show Reprints"
					color="primary"
					class="ma-0"
				/>
				<v-checkbox
					v-model="output.config.useAllKeywordLists"
					hide-details
					label="Use All Keyword Lists"
					color="primary"
					class="ma-0"
				/>
				<div class="setting-item">
					<v-radio-group v-model="output.config.headerImage" hide-details>
						<div class="v-label">Header Image</div>
						<v-radio
							v-for="option in headerImageOptions"
							:key="option.name"
							:label="option.name"
							:value="option.value"
							color="primary"
						/>
					</v-radio-group>
				</div>
				<div class="setting-item">
					<v-layout
						row
						align-center
					>
						<v-flex py-0>
							<v-select
								v-model="output.sort1Attr"
								:items="primarySort"
								:rules="[sortSiblingNotEmpty('primary'), v => !!v || 'Field is required']"
								label="Primary Sort *"
								item-text="name"
								item-value="value"
								@change="changePrimary"
							/>
						</v-flex>
						<v-btn
							:disabled="!output.sort1Attr"
							flat
							icon
							color="primary"
							@click="output.sort1Desc = !output.sort1Desc"
						>
							<v-icon v-if="!output.sort1Desc">fa fa-sort-alpha-asc</v-icon>
							<v-icon v-else>fa fa-sort-alpha-desc</v-icon>
						</v-btn>
					</v-layout>
					<v-layout
						v-show="output.sort1Attr || output.sort2Attr || output.sort3Attr"
						row
						align-center
					>
						<v-flex py-0>
							<v-select
								ref="sort2"
								v-model="output.sort2Attr"
								:items="secondarySort"
								:rules="[sortSiblingNotEmpty('secondary')]"
								label="Secondary Sort"
								item-text="name"
								item-value="value"
								@change="changeSecondary()"
							/>
						</v-flex>
						<v-btn
							:disabled="!output.sort2Attr"
							flat
							icon
							color="primary"
							@click="output.sort2Desc = !output.sort2Desc"
						>
							<v-icon v-if="!output.sort2Desc">fa fa-sort-alpha-asc</v-icon>
							<v-icon v-else>fa fa-sort-alpha-desc</v-icon>
						</v-btn>
					</v-layout>
					<v-layout
						v-show="output.sort2Attr || output.sort3Attr"
						row
						align-center
					>
						<v-flex py-0>
							<v-select
								v-model="output.sort3Attr"
								:items="tertiarySort"
								label="Tertiary Sort"
								item-text="name"
								item-value="value"
							/>
						</v-flex>
						<v-btn
							:disabled="!output.sort3Attr"
							flat
							icon
							color="primary"
							@click="output.sort3Desc = !output.sort3Desc"
						>
							<v-icon v-if="!output.sort3Desc">fa fa-sort-alpha-asc</v-icon>
							<v-icon v-else>fa fa-sort-alpha-desc</v-icon>
						</v-btn>
					</v-layout>
				</div>
			</div>
			<slot name="additional-settings" />
			<div
				v-if="$userCan('change_output_settings')"
				class="text-xs-center pa-2 mt-3"
			>
				<v-btn
					:disabled="disableSubmit"
					ripple
					class="primary"
					@click="submit"
				>
					save
				</v-btn>
				<slot name="additional-actions" />
			</div>
		</v-form>
	</v-container>
</template>

<script>
import { Output } from '@/models/Output';

export default {
	props: {
		outputSettings: {
			type: Object,
			default: () => new Output(),
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			valid: true,
			output: null,
			articleDisplays: [
				{ name: 'Show Body', value: 'body' },
				{ name: 'Show Summary', value: 'summary' },
				{ name: 'Show Both', value: 'both' },
				{ name: 'No Body', value: 'none' },
			],
			headerImageOptions: [
				{ name: 'Project Image', value: 'project' },
				{ name: 'Report Image', value: 'report' },
				{ name: 'No Image', value: 'no' },
			],
			emptySortBy: { name: '', value: null },
		};
	},
	computed: {
		sortBy() {
			const sortBy = this.$store.get('outputSortSettings');
			return sortBy;
		},
		disableSubmit() {
			if (this.disabled || !this.valid) {
				return true;
			}
			return false;
		},
		primarySort() {
			return this.sortBy;
		},
		secondarySort() {
			if (!this.output.sort1Attr) {
				return this.sortBy;
			}
			const sort = this.sortBy.filter(item => (item.value !== this.output.sort1Attr));
			sort.unshift(this.emptySortBy);
			return sort;
		},
		tertiarySort() {
			if (!this.output.sort2Attr) {
				return this.secondarySort;
			}
			const sort = this.secondarySort.filter(item => (item.value !== this.output.sort2Attr));
			sort.unshift(this.emptySortBy);
			return sort;
		},
	},
	watch: {
		outputSettings: {
			handler(val) {
				this.output = val;
			},
			immediate: true,
		},
	},
	methods: {
		reset() {
			this.output = new Output();
			this.$refs.form.reset();
		},
		validate() {
			this.$refs.form.validate();
			if (this.disableSubmit) {
				return;
			}
			this.submit();
		},
		submit() {
			// Submit logic here emit to either OutputNew or OutputView
			this.$emit('output-settings', this.output);
			// this.$refs.form.reset();
		},
		sortSiblingNotEmpty(parent) {
			if (parent === 'primary') {
				if (!this.output.sort2Attr && !this.output.sort3Attr) {
					return true;
				}
				if (this.output.sort1Attr === null
				&& (this.output.sort2Attr !== null || this.output.sort3Attr !== null)) {
					return 'Field cannot be empty';
				}
				return true;
			}
			if (parent === 'secondary') {
				if (this.output.sort3Attr !== null && this.output.sort2Attr === null) {
					return 'Field cannot be empty';
				}
				if (this.output.sort3Attr === null && this.output.sort2Attr === null) {
					if (this.$refs.sort2) {
						this.$refs.sort2.reset();
					}
				}
				return true;
			}
			return true;
		},
		changePrimary() {
			const primary = this.output.sort1Attr;
			if (this.output.sort2Attr === primary) {
				this.output.sort2Attr = null;
			}
			else if (this.output.sort3Attr === primary) {
				this.output.sort3Attr = null;
			}
		},
		changeSecondary() {
			const secondary = this.output.sort2Attr;
			if (this.output.sort3Attr === secondary) {
				this.output.sort3Attr = null;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.setting-item {
	padding: 15px 0 0;
}
</style>
